//#region Util Imports

import * as _ from 'underscore'
import moment = require('moment')

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'
import { dxSelectBoxOptions } from 'devextreme/ui/select_box'
import { dxValidatorOptions } from 'devextreme/ui/validator'
import { dxRadioGroupOptions } from 'devextreme/ui/radio_group'
import { dxNumberBoxOptions } from 'devextreme/ui/number_box'
import { dxDateBoxOptions } from 'devextreme/ui/date_box'
import { dxCheckBoxOptions } from 'devextreme/ui/check_box'
import { dxFormSimpleItem } from 'devextreme/ui/form'
import { PatternRule, RequiredRule } from 'devextreme/ui/validation_engine'

//#endregion

//#region IMR Imports

import { IMRPopupOptions, IIMRPopup } from '@imr/controls.angularjs/components/overlay/popup'
import { IMRComponentInitializedArgs } from '@imr/controls.angularjs/components/base'

//#endregion

import { Enums } from '../../../../../models/common'
import { OpData } from '../../../../../models/core'
import { UserSessionService } from '../../../../../services/user-session'

import { Dictionary, KeyType, LocalizationHelper, TypeHelper } from '../../../../../helpers'

import { IMRBaseControlController } from '../../../base'

import { CorrespondingActionTypeModel } from '../../../../api/api.models'
import { AstGsmActionOptions, IAstGsmAction } from './action-run.dedicated.ast-gsm.interfaces'
import { ReferenceTypeService } from '../../../../../services/reference-type'

interface CommandFormData extends PopCommandData, PRZCommandData, AstronomicalData, SetChangeTimeData {
	output?: string
	command?: string
}

interface PopCommandData {
	time?: number
	time1?: number
	time2?: number
	time3?: number
	time4?: number
}

interface PRZCommandData {
	t1s?: Date
	t1e?: Date
	t2s?: Date
	t2e?: Date
	t3s?: Date
	t3e?: Date
	t4s?: Date
	t4e?: Date
}

interface AstronomicalData {
	astro1?: boolean
	astro2?: boolean
	astro3?: boolean
	astro4?: boolean
	astro5?: boolean
	astro6?: boolean
	astro7?: boolean
	astro8?: boolean
}

interface SetChangeTimeData {
	setChangeTime?: boolean
}

enum IndexName {
	A = 0,
	B = 1,
	C = 2,
	D = 3,
	E = 4,
	F = 5,
}

export class AstGsmActionController extends IMRBaseControlController<IAstGsmAction, AstGsmActionOptions> implements ng.IComponentController, IAstGsmAction {
	public get LOG_PREFIX(): string {
		return '[SIMAX.Common.ACTION-RUN.DEDICATED.AST_GSM_ACTION]'
	}

	private readonly timeFormatExpr: string = 'HH:mm:ss'

	private readonly dtFieldOutput: string = 'output'
	private readonly dtFieldCommand: string = 'command'

	private readonly dtFieldTime: string = 'time'

	private readonly dtFieldTime1: string = 'time1'
	private readonly dtFieldTime2: string = 'time2'
	private readonly dtFieldTime3: string = 'time3'
	private readonly dtFieldTime4: string = 'time4'

	private readonly dtFieldTime1start: string = 't1s'
	private readonly dtFieldTime1end: string = 't1e'
	private readonly dtFieldTime2start: string = 't2s'
	private readonly dtFieldTime2end: string = 't2e'
	private readonly dtFieldTime3start: string = 't3s'
	private readonly dtFieldTime3end: string = 't3e'
	private readonly dtFieldTime4start: string = 't4s'
	private readonly dtFieldTime4end: string = 't4e'

	private readonly dtFieldAstro1: string = 'astro1'
	private readonly dtFieldAstro2: string = 'astro2'
	private readonly dtFieldAstro3: string = 'astro3'
	private readonly dtFieldAstro4: string = 'astro4'
	private readonly dtFieldAstro5: string = 'astro5'
	private readonly dtFieldAstro6: string = 'astro6'
	private readonly dtFieldAstro7: string = 'astro7'
	private readonly dtFieldAstro8: string = 'astro8'

	private readonly dtSetChangeTime: string = 'setChangeTime'

	private readonly inputA: string = 'A'
	private readonly inputB: string = 'B'
	private readonly inputC: string = 'C'
	private readonly inputD: string = 'D'
	private readonly inputE: string = 'E'
	private readonly inputF: string = 'F'

	private readonly commandON: string = 'ON'
	private readonly commandONT: string = 'ONT'
	private readonly commandOFF: string = 'OFF'
	private readonly commandAUTO: string = 'AUTO'
	private readonly commandPOP: string = 'POP'
	private readonly commandPRZ: string = 'PRZ'
	private readonly commandSetChangeTime: string = 'SETCHANGETIME'

	private readonly popGroup: string = 'popGroup'
	private readonly przGroup: string = 'przGroup'
	private readonly setChangeTimeGroup: string = 'setChangeTimeGroup'

	public popupOptions: IMRPopupOptions
	public showLoader: boolean = false
	public formOptions: DevExpress.ui.dxFormOptions
	public formData: CommandFormData
	public inputData: CommandFormData

	private form: DevExpress.ui.dxForm

	public devicePortSelectBoxOptions: DevExpress.ui.dxSelectBoxOptions

	public showActionRunOptions: boolean

	public readonly astronomicalModeExplainText: string = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AstronomicalModeExplain)
	public readonly photoModeExplainText: string = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.PhotoModeExplain)
	public readonly hybrydModeExplainText: string = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.HybrydModeExplain)
	public readonly dimmingModeExplainText: string = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.DimmingModeExplain)
	public readonly operatingModeText: string = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.OperatingMode)

	public operatingModeSelectBoxOptions: dxSelectBoxOptions
	public operatingModeValidatorOptions: dxValidatorOptions

	public timeRangeSource: { id: number; caption: string }[] = []

	private action: CorrespondingActionTypeModel
	private popup: IIMRPopup

	private correctionValues: Dictionary<CommandFormData> = new Dictionary<CommandFormData>([], KeyType.Number)

	constructor(private readonly userSessionService: UserSessionService, protected readonly referenceTypeService: ReferenceTypeService) {
		super()
	}

	protected initialize(options: AstGsmActionOptions): JQueryPromise<any> {
		this.setAction(options.action)

		return $.when(this.initializepPopupOptions())
	}

	// #region Popup

	private initializepPopupOptions(): JQueryPromise<any> {
		this.popupOptions = {
			innerOptions: {
				title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AstGsmAction),
				deferRendering: false,
				onHiding: () => this.onPopupHiding(),
				fullScreen: false,
				maxWidth: 700,
				maxHeight: 800,
			},

			onInitialized: (e: IMRComponentInitializedArgs<IIMRPopup>) => {
				this.initializePopup(e.component)
			},

			bottomToolbarOptions: {
				items: [
					{
						location: 'after',
						widget: 'dxButton',
						options: {
							text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Send),
							hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Send),
							onClick: () => {
								this.onSubmit()
							},
						},
					},
					{
						location: 'after',
						widget: 'dxButton',
						options: {
							text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Cancel),
							hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Cancel),
							onClick: () => {
								this.popup.hide()
							},
						},
					},
				],
			},
		}

		return super.waitForOnInitialized(this.popupOptions)
	}

	// #endregion

	// #region Private Methods

	private onPopupHiding(): void {
		this.showLoader = false

		if (_.isFunction(this.options.onHiding)) {
			this.options.onHiding()
		}

		this.clear()
	}

	private clear(): void {
		this.formOptions = null
		this.formData = null
		this.inputData = null
		this.correctionValues = new Dictionary<CommandFormData>([], KeyType.Number)
	}

	private initializePopup(component: IIMRPopup): void {
		this.popup = component
	}

	private onSubmit(): void {
		if (_.isFunction(this.options.onActionRunClick)) {
			if (this.form != null && this.form.validate().isValid === true) {
				let commandToSend: string = ''

				const formData: CommandFormData = this.formData
				if (formData.command == this.commandONT) {
					commandToSend = `${formData.output}${this.commandON}:${formData.time ? formData.time * 60 : 0}`
				} else if (formData.command == this.commandPOP) {
					commandToSend = `${formData.output}${formData.command}:${formData.time1 ? formData.time1 * 60 : 0},${formData.time2 ? formData.time2 * 60 : 0},${formData.time3 ? formData.time3 * 60 : 0},${
						formData.time4 ? formData.time4 * 60 : 0
					}`
				} else if (formData.command == this.commandPRZ) {
					commandToSend = `${formData.output}${formData.command}:${formData[this.dtFieldAstro1] == true ? -1 : this.getFullSeconds(formData[this.dtFieldTime1start])},${
						formData[this.dtFieldAstro2] == true ? -1 : this.getFullSeconds(formData[this.dtFieldTime1end])
					},${formData[this.dtFieldAstro3] == true ? -1 : this.getFullSeconds(formData[this.dtFieldTime2start])},${
						formData[this.dtFieldAstro4] == true ? -1 : this.getFullSeconds(formData[this.dtFieldTime2end])
					},${formData[this.dtFieldAstro5] == true ? -1 : this.getFullSeconds(formData[this.dtFieldTime3start])},${
						formData[this.dtFieldAstro6] == true ? -1 : this.getFullSeconds(formData[this.dtFieldTime3end])
					},${formData[this.dtFieldAstro7] == true ? -1 : this.getFullSeconds(formData[this.dtFieldTime4start])},${
						formData[this.dtFieldAstro8] == true ? -1 : this.getFullSeconds(formData[this.dtFieldTime4end])
					},`
				} else if (formData.command == this.commandSetChangeTime) {
					commandToSend = `${formData.command}:${formData.setChangeTime == true ? 1 : 0}`
				} else {
					commandToSend = formData.output + formData.command
				}

				this.options.onActionRunClick(commandToSend)
				this.popup.showLoading()
			}
		}
	}

	private getFullSeconds(date: Date): number {
		if (date == null || date.getDate == null) {
			return 0
		}

		return date.getHours() * 60 * 60 + date.getMinutes() * 60 + date.getSeconds()
	}

	// #endregion

	// #region ASTgsmAction

	public showPopup(): void {
		this.popup.show()
		this.popup.showLoading()
		this.prepareForm()
	}

	public setAction(action: CorrespondingActionTypeModel): void {
		this.action = TypeHelper.clone(action, true)
	}

	public hidePopup(): void {
		this.popup.hide()
	}

	// #endregion

	//#region Form

	private prepareForm(): void {
		//FORM

		const deferred: JQueryDeferred<CommandFormData> = $.Deferred<CommandFormData>()

		if (_.size(this.action.referenceValues) == 1) {
			//request

			this.userSessionService
				.getDataProvider()
				.Device.OpxGetAstGsmDeviceConfigurationData(this.action.referenceValues[0])
				.then((data: OpData[]) => {
					_.forEach(data, (d: OpData) => {
						let value: CommandFormData = this.correctionValues.tryGetValue(d.indexNbr)

						if (value == null) {
							value = { time: null, time1: null, time2: null, time3: null, time4: null }
						}

						switch (d.idDataType) {
							//#region POP
							case Enums.DataType.DEVICE_CORRECTION_OF_WINTER_ACTIVATION:
								value.time1 = Math.floor(d.value / 60)
								break
							case Enums.DataType.DEVICE_CORRECTION_OF_WINTER_SHUTDOWN:
								value.time2 = Math.floor(d.value / 60)
								break
							case Enums.DataType.DEVICE_CORRECTION_OF_SUMMER_ACTIVATION:
								value.time3 = Math.floor(d.value / 60)
								break
							case Enums.DataType.DEVICE_CORRECTION_OF_SUMMER_SHUTDOWN:
								value.time4 = Math.floor(d.value / 60)
								break
							//#endregion
							//#region PRZ
							case Enums.DataType.DEVICE_INTERVAL_1_ACTIVATION:
								if (d.value != -1) {
									value.t1s = this.setSecondsToDate(d.value)
								} else {
									value.astro1 = true
								}
								break
							case Enums.DataType.DEVICE_INTERVAL_1_SHUTDOWN:
								if (d.value != -1) {
									value.t1e = this.setSecondsToDate(d.value)
								} else {
									value.astro2 = true
								}
								break
							case Enums.DataType.DEVICE_INTERVAL_2_ACTIVATION:
								if (d.value != -1) {
									value.t2s = this.setSecondsToDate(d.value)
								} else {
									value.astro3 = true
								}
								break
							case Enums.DataType.DEVICE_INTERVAL_2_SHUTDOWN:
								if (d.value != -1) {
									value.t2e = this.setSecondsToDate(d.value)
								} else {
									value.astro4 = true
								}
								break
							case Enums.DataType.DEVICE_INTERVAL_3_ACTIVATION:
								if (d.value != -1) {
									value.t3s = this.setSecondsToDate(d.value)
								} else {
									value.astro5 = true
								}
								break
							case Enums.DataType.DEVICE_INTERVAL_3_SHUTDOWN:
								if (d.value != -1) {
									value.t3e = this.setSecondsToDate(d.value)
								} else {
									value.astro6 = true
								}
								break
							case Enums.DataType.DEVICE_INTERVAL_4_ACTIVATION:
								if (d.value != -1) {
									value.t4s = this.setSecondsToDate(d.value)
								} else {
									value.astro7 = true
								}
								break
							case Enums.DataType.DEVICE_INTERVAL_4_SHUTDOWN:
								if (d.value != -1) {
									value.t4e = this.setSecondsToDate(d.value)
								} else {
									value.astro8 = true
								}
								break
							//#endregion
						}

						this.correctionValues.addOrUpdate(d.indexNbr, value)
					})

					deferred.resolve({ time: null, time1: null, time2: null, time3: null, time4: null })
				})
		} else {
			deferred.resolve({ time: null, time1: null, time2: null, time3: null, time4: null })
		}

		deferred.promise().then((formData: CommandFormData) => {
			//#region Form data
			this.formData = formData
			this.inputData = TypeHelper.clone(formData, true)
			//#endregion

			this.createForm()
		})
	}

	private createForm(): void {
		this.formData = {}

		this.formOptions = <DevExpress.ui.dxFormOptions>{
			onFieldDataChanged: (e: any) => {
				if (e.dataField == this.dtFieldAstro1) {
					this.form.itemOption(`${this.przGroup}.${this.przGroup}0.${this.dtFieldTime1start}`, <DevExpress.ui.dxFormSimpleItem>{
						editorOptions: { ...this.getPrzDateEditorOptions(), disabled: e.value },
					})
				}

				if (e.dataField == this.dtFieldAstro2) {
					this.form.itemOption(`${this.przGroup}.${this.przGroup}0.${this.dtFieldTime1end}`, <DevExpress.ui.dxFormSimpleItem>{
						editorOptions: { ...this.getPrzDateEditorOptions(), disabled: e.value },
					})
				}

				if (e.dataField == this.dtFieldAstro3) {
					this.form.itemOption(`${this.przGroup}.${this.przGroup}1.${this.dtFieldTime2start}`, <DevExpress.ui.dxFormSimpleItem>{
						editorOptions: { ...this.getPrzDateEditorOptions(), disabled: e.value },
					})
				}

				if (e.dataField == this.dtFieldAstro4) {
					this.form.itemOption(`${this.przGroup}.${this.przGroup}1.${this.dtFieldTime2end}`, <DevExpress.ui.dxFormSimpleItem>{
						editorOptions: { ...this.getPrzDateEditorOptions(), disabled: e.value },
					})
				}

				if (e.dataField == this.dtFieldAstro5) {
					this.form.itemOption(`${this.przGroup}.${this.przGroup}2.${this.dtFieldTime3start}`, <DevExpress.ui.dxFormSimpleItem>{
						editorOptions: { ...this.getPrzDateEditorOptions(), disabled: e.value },
					})
				}

				if (e.dataField == this.dtFieldAstro6) {
					this.form.itemOption(`${this.przGroup}.${this.przGroup}2.${this.dtFieldTime3end}`, <DevExpress.ui.dxFormSimpleItem>{
						editorOptions: { ...this.getPrzDateEditorOptions(), disabled: e.value },
					})
				}

				if (e.dataField == this.dtFieldAstro7) {
					this.form.itemOption(`${this.przGroup}.${this.przGroup}3.${this.dtFieldTime4start}`, <DevExpress.ui.dxFormSimpleItem>{
						editorOptions: { ...this.getPrzDateEditorOptions(), disabled: e.value },
					})
				}

				if (e.dataField == this.dtFieldAstro8) {
					this.form.itemOption(`${this.przGroup}.${this.przGroup}3.${this.dtFieldTime4end}`, <DevExpress.ui.dxFormSimpleItem>{
						editorOptions: { ...this.getPrzDateEditorOptions(), disabled: e.value },
					})
				}
			},

			requiredMark: '*',
			showRequiredMark: true,
			labelLocation: 'top',
			colCount: 1,
			width: '100%',
			height: '100%',
			scrollingEnabled: true,
			items: this.getItems(),
			bindingOptions: {
				formData: '$ctrl.formData',
			},
			onInitialized: (e: any) => {
				this.form = e.component
				this.popup.hideLoading()
			},
		}
	}

	private getItems(): dxFormSimpleItem[] {
		return <any[]>[
			{
				dataField: this.dtFieldOutput,
				name: this.dtFieldOutput,
				visible: true,
				label: {
					text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Output),
				},
				validationRules: [this.getValidationPattern(), this.getValidationRequired()],
				editorType: 'dxSelectBox',
				editorOptions: this.getOutputEditorOptions(),
			},
			{
				dataField: this.dtFieldCommand,
				name: this.dtFieldCommand,
				visible: true,
				label: {
					text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Command),
				},
				validationRules: [this.getValidationPattern(), this.getValidationRequired()],
				editorType: 'dxRadioGroup',
				editorOptions: <dxRadioGroupOptions>{
					items: [
						{ caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.CommandOnDescription), value: this.commandON },
						{ caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.CommandOnWithTimeDescription), value: this.commandONT },
						{ caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.CommandOffDescription), value: this.commandOFF },
						{ caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.CommandAutoDescription), value: this.commandAUTO },
						{ caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.CommandPopDescription), value: this.commandPOP },
						{ caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.CommandPRZDescription), value: this.commandPRZ },
						{ caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.WinterSummerManagement), value: this.commandSetChangeTime },
					],
					displayExpr: 'caption',
					valueExpr: 'value',
					onValueChanged: (e: any) => {
						this.form.itemOption(this.dtFieldTime, { visible: e.value == this.commandONT })
						this.form.itemOption(this.popGroup, { visible: e.value == this.commandPOP })
						this.form.itemOption(this.przGroup, { visible: e.value == this.commandPRZ })
						this.form.itemOption(this.setChangeTimeGroup, { visible: e.value == this.commandSetChangeTime })
						this.form.itemOption(this.dtFieldOutput, { editorOptions: { ...this.getOutputEditorOptions(), disabled: e.value == this.commandSetChangeTime } })
					},
				},
			},
			{
				dataField: this.dtFieldTime,
				name: this.dtFieldTime,
				visible: false,
				cssClass: 'timeField',
				label: {
					text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.TimeMinute),
				},
				validationRules: [this.getValidationPattern(), this.getValidationRequired()],
				editorType: 'dxNumberBox',
				editorOptions: <dxNumberBoxOptions>{
					min: 0,
				},
			},
			<DevExpress.ui.dxFormGroupItem>{
				itemType: 'group',
				name: this.popGroup,
				visible: false,
				colCount: 2,
				cssClass: 'popGroup',
				items: [
					{
						dataField: this.dtFieldTime1,

						visible: true,
						label: {
							text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.CorrectionOnWinter),
						},
						validationRules: [this.getValidationPattern(), this.getValidationRequired()],
						editorType: 'dxNumberBox',
						editorOptions: <dxNumberBoxOptions>{},
					},
					{
						dataField: this.dtFieldTime2,

						visible: true,
						label: {
							text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.CorrectionOffWinter),
						},
						validationRules: [this.getValidationPattern(), this.getValidationRequired()],
						editorType: 'dxNumberBox',
						editorOptions: <dxNumberBoxOptions>{},
					},
					{
						dataField: this.dtFieldTime3,

						visible: true,
						label: {
							text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.CorrectionOnSummer),
						},
						validationRules: [this.getValidationPattern(), this.getValidationRequired()],
						editorType: 'dxNumberBox',
						editorOptions: <dxNumberBoxOptions>{},
					},
					{
						dataField: this.dtFieldTime4,

						visible: true,
						label: {
							text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.CorrectionOffSummer),
						},
						validationRules: [this.getValidationPattern(), this.getValidationRequired()],
						editorType: 'dxNumberBox',
						editorOptions: <dxNumberBoxOptions>{},
					},
				],
			},
			<DevExpress.ui.dxFormGroupItem>{
				itemType: 'group',
				name: this.przGroup,
				visible: false,
				colCount: 2,
				cssClass: 'przGroup',
				items: [
					<DevExpress.ui.dxFormGroupItem>{
						itemType: 'group',
						name: `${this.przGroup}0`,
						caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Compartment1),
						colCount: 2,
						cssClass: 'border',
						items: [
							{
								dataField: this.dtFieldTime1start,
								name: this.dtFieldTime1start,
								label: {
									text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Start),
								},
								validationRules: [this.getValidationPattern(), this.getValidationRequired()],
								editorType: 'dxDateBox',
								editorOptions: this.getPrzDateEditorOptions(),
							},
							{
								dataField: this.dtFieldAstro1,
								label: {
									text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Astro),
								},
								validationRules: [this.getValidationPattern()],
								editorType: 'dxCheckBox',
								editorOptions: <dxCheckBoxOptions>{},
							},
							{
								dataField: this.dtFieldTime1end,
								name: this.dtFieldTime1end,
								label: {
									text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.End),
								},
								validationRules: [this.getValidationPattern(), this.getValidationRequired()],
								editorType: 'dxDateBox',
								editorOptions: this.getPrzDateEditorOptions(),
							},
							{
								dataField: this.dtFieldAstro2,
								label: {
									text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Astro),
								},
								validationRules: [this.getValidationPattern()],
								editorType: 'dxCheckBox',
								editorOptions: <dxCheckBoxOptions>{},
							},
						],
					},
					<DevExpress.ui.dxFormGroupItem>{
						itemType: 'group',
						name: `${this.przGroup}1`,
						caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Compartment2),
						visible: true,
						colCount: 2,
						cssClass: 'border',
						items: [
							{
								dataField: this.dtFieldTime2start,
								name: this.dtFieldTime2start,
								label: {
									text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Start),
								},
								validationRules: [this.getValidationPattern(), this.getValidationRequired()],
								editorType: 'dxDateBox',
								editorOptions: this.getPrzDateEditorOptions(),
							},
							{
								dataField: this.dtFieldAstro3,
								label: {
									text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Astro),
								},
								validationRules: [this.getValidationPattern()],
								editorType: 'dxCheckBox',
								editorOptions: <dxCheckBoxOptions>{},
							},
							{
								dataField: this.dtFieldTime2end,
								name: this.dtFieldTime2end,
								label: {
									text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.End),
								},
								validationRules: [this.getValidationPattern(), this.getValidationRequired()],
								editorType: 'dxDateBox',
								editorOptions: this.getPrzDateEditorOptions(),
							},
							{
								dataField: this.dtFieldAstro4,
								label: {
									text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Astro),
								},
								validationRules: [this.getValidationPattern()],
								editorType: 'dxCheckBox',
								editorOptions: <dxCheckBoxOptions>{},
							},
						],
					},
					<DevExpress.ui.dxFormGroupItem>{
						itemType: 'group',
						name: `${this.przGroup}2`,
						caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Compartment3),
						visible: true,
						colCount: 2,
						cssClass: 'border',
						items: [
							{
								dataField: this.dtFieldTime3start,
								name: this.dtFieldTime3start,
								label: {
									text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Start),
								},
								validationRules: [this.getValidationPattern(), this.getValidationRequired()],
								editorType: 'dxDateBox',
								editorOptions: this.getPrzDateEditorOptions(),
							},
							{
								dataField: this.dtFieldAstro5,
								label: {
									text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Astro),
								},
								validationRules: [this.getValidationPattern()],
								editorType: 'dxCheckBox',
								editorOptions: <dxCheckBoxOptions>{},
							},
							{
								dataField: this.dtFieldTime3end,
								name: this.dtFieldTime3end,
								label: {
									text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.End),
								},
								validationRules: [this.getValidationPattern(), this.getValidationRequired()],
								editorType: 'dxDateBox',
								editorOptions: this.getPrzDateEditorOptions(),
							},
							{
								dataField: this.dtFieldAstro6,
								label: {
									text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Astro),
								},
								validationRules: [this.getValidationPattern()],
								editorType: 'dxCheckBox',
								editorOptions: <dxCheckBoxOptions>{},
							},
						],
					},
					<DevExpress.ui.dxFormGroupItem>{
						itemType: 'group',
						name: `${this.przGroup}3`,
						caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Compartment4),
						colCount: 2,
						cssClass: 'border',
						items: [
							{
								dataField: this.dtFieldTime4start,
								name: this.dtFieldTime4start,
								label: {
									text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Start),
								},
								validationRules: [this.getValidationPattern(), this.getValidationRequired()],
								editorType: 'dxDateBox',
								editorOptions: this.getPrzDateEditorOptions(),
							},
							{
								dataField: this.dtFieldAstro7,
								label: {
									text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Astro),
								},
								validationRules: [this.getValidationPattern()],
								editorType: 'dxCheckBox',
								editorOptions: <dxCheckBoxOptions>{},
							},
							{
								dataField: this.dtFieldTime4end,
								name: this.dtFieldTime4end,
								label: {
									text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.End),
								},
								validationRules: [this.getValidationPattern(), this.getValidationRequired()],
								editorType: 'dxDateBox',
								editorOptions: this.getPrzDateEditorOptions(),
							},
							{
								dataField: this.dtFieldAstro8,
								label: {
									text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Astro),
								},
								validationRules: [this.getValidationPattern()],
								editorType: 'dxCheckBox',
								editorOptions: <dxCheckBoxOptions>{},
							},
						],
					},
				],
			},
			<DevExpress.ui.dxFormGroupItem>{
				itemType: 'group',
				name: this.setChangeTimeGroup,
				visible: false,
				items: [
					{
						dataField: this.dtSetChangeTime,
						label: {
							text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.TurnOn),
						},
						validationRules: [this.getValidationPattern()],
						editorType: 'dxCheckBox',
						editorOptions: <dxCheckBoxOptions>{},
					},
				],
			},
		]
	}

	private onExitValueChanged(e: any): void {
		if (e.value != null) {
			const predefinedCorrection: CommandFormData = this.correctionValues.tryGetValue(IndexName[e.value])

			if (predefinedCorrection != null) {
				this.formData = <any>{ ...this.formData, ...predefinedCorrection }
			}
		}
	}

	private getValidationPattern(): PatternRule {
		return {
			type: 'pattern',
			pattern: '',
		}
	}

	private getValidationRequired(): RequiredRule {
		return {
			type: 'required',
			message: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.RequiredField),
		}
	}

	private getOutputEditorOptions(): dxSelectBoxOptions {
		return <dxSelectBoxOptions>{
			items: [this.inputA, this.inputB, this.inputC, this.inputD, this.inputE, this.inputF],
			onValueChanged: (e: any) => {
				this.onExitValueChanged(e)
			},
		}
	}
	private getPrzDateEditorOptions(): dxDateBoxOptions {
		return <DevExpress.ui.dxDateBoxOptions>{
			type: 'time',
			interval: 1,
			displayFormat: this.timeFormatExpr,
		}
	}

	private setSecondsToDate(value: number): Date {
		const date: Date = new Date(0, 0, 0, 0, 0, 0, 0)

		if (value == null) {
			return date
		}

		date.setSeconds(value)
		return date
	}

	//#endregion
}
